// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useMemo, useState } from 'react';
import { getSlaFormatYearMonthDate, getUtcFormatDateTime } from 'ts/formatDate';
import {
  EmissionStatus,
  GetEmissionParams,
  UncollectEmission,
} from 'util/Types';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import styles from './UnCollectEmissions.module.scss';
import { useTranslation } from 'react-i18next';
import {
  DATE_FORMAT,
  DEFAULT_PAGE,
  TEMPORARY_STORAGE_COLORS_PALETTE,
} from 'util/ConstantValues';
import UnCollectChainTag from 'components/atoms/UnCollectChainTag';
import {
  getBrightnessFromColor,
  mergeClasses,
  normalizeNumber,
} from 'util/commons';
import {
  OptionItem,
  SelectFieldMultipleChoise,
} from 'components/molecules/SelectFieldMultipleChoise';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';

interface Props {
  emissions: UncollectEmission[];
  itemOptions: OptionItem[];
  tenantOptions: OptionItem[];
  temporaryStorageOptions: OptionItem[];
  filter?: GetEmissionParams;
  onChange?: (checkedItems: string[]) => void;
  callApi: (params?: {
    page?: number;
    filterValue?: GetEmissionParams;
  }) => void;
}

export function UnCollectEmissions({
  emissions,
  itemOptions,
  tenantOptions,
  temporaryStorageOptions,
  filter,
  onChange,
  callApi,
}: Props) {
  const [checkedItems, setCheckedItems] = useState({});
  const [allCheckedFlg, setAllCheckedFlg] = useState(false);
  const [tmpFilter, setTmpFilter] = useState<GetEmissionParams>(filter || {});
  const user = useSelector(selectUser);

  const { t } = useTranslation();

  const checkableEmissions = useMemo(
    () => emissions.filter(({ nextStage }) => nextStage),
    [emissions]
  );

  useEffect(() => {
    const checkedList = Object.keys(checkedItems).filter(
      (key) => checkedItems[key] == true
    );

    if (onChange) {
      onChange(checkedList);
    }

    setAllCheckedFlg(
      checkedList.length > 0 && checkedList.length >= checkableEmissions.length
    );
  }, [checkedItems, setAllCheckedFlg]);

  useEffect(() => {
    setCheckedItems({});
    setAllCheckedFlg(!allCheckedFlg);
  }, [emissions]);

  const handleChange = (e) => {
    setCheckedItems({
      ...checkedItems,
      [e.target.id]: e.target.checked,
    });
  };

  const allChecked = () => {
    if (allCheckedFlg) {
      // 全チェック -> 全チェック外し
      Object.keys(checkedItems).forEach(function (key) {
        checkedItems[key] = false;
      });
      setCheckedItems(checkedItems);
      if (onChange) {
        onChange([]);
      }
    } else {
      checkableEmissions.forEach(function (emission) {
        checkedItems[emission.id] = true;
      });
      // 全チェック
      setCheckedItems(checkedItems);
      if (onChange) {
        onChange(checkableEmissions.map((v) => v.id));
      }
    }
    setAllCheckedFlg(!allCheckedFlg);
  };

  const onChangeFilter = (key: string, value: string[]) => {
    setTmpFilter({ ...tmpFilter, [key]: value });
  };

  const handleSubmitFilter = () => {
    callApi({ page: DEFAULT_PAGE, filterValue: tmpFilter });
  };

  const tenantCustomOptions = useMemo(() => {
    const unidentifyTenant: OptionItem = {
      id: 0,
      name: t('un_collect.unidentify_tenant'),
    };

    return tenantOptions.concat(unidentifyTenant);
  }, [tenantOptions, t]);

  const tempStorageCustomOptions = useMemo(() => {
    const notSetOption: OptionItem = {
      id: '',
      name: t('un_collect.not_set_temp_storage'),
    };

    return temporaryStorageOptions.concat(notSetOption);
  }, [temporaryStorageOptions, t]);

  const chainStatusOptions = useMemo((): OptionItem[] => {
    return [
      {
        id: EmissionStatus.UnRequest,
        name: t('un_collect.chain_status_options.with_chain'),
      },
      {
        id: EmissionStatus.Close,
        name: t('un_collect.chain_status_options.no_chain'),
      },
    ];
  }, [t]);

  return (
    <div className={styles.unCollectList}>
      <div className={styles.unCollectFilter}>
        <div
          className={mergeClasses(styles.filterWrapper, {
            [styles.filterWithTempStorage]: user.temporaryStorageEnabled,
          })}
        >
          <div className={styles.unCollectFilterBox}>
            <SelectFieldMultipleChoise
              options={itemOptions}
              name="Items"
              searchable={true}
              placeholder={t('common.item') ?? ''}
              onChange={(items) =>
                onChangeFilter(
                  'wasteUnitNames',
                  items.map((item) => String(item.name))
                )
              }
              selectedOptions={
                itemOptions.filter((obj) =>
                  tmpFilter?.wasteUnitNames?.includes(String(obj.name))
                ) || []
              }
              darkMode
              allowClear
            />
          </div>

          <div className={styles.unCollectFilterBox}>
            <SelectFieldMultipleChoise
              options={tenantCustomOptions}
              name="Tenants"
              searchable={true}
              placeholder={t('tenant.thead_tenant') ?? ''}
              onChange={(tenants) =>
                onChangeFilter(
                  'tenantIds',
                  tenants.map((tenant) => String(tenant.id))
                )
              }
              selectedOptions={
                tenantCustomOptions.filter((obj) =>
                  tmpFilter?.tenantIds?.includes(String(obj.id))
                ) || []
              }
              darkMode
              allowClear
            />
          </div>

          <div className={styles.unCollectFilterBox}>
            <SelectFieldMultipleChoise
              options={chainStatusOptions}
              name="chainStatus"
              placeholder={t('un_collect.chain_status') ?? ''}
              onChange={(chain_statuses) =>
                onChangeFilter(
                  'status',
                  chain_statuses.map((status) => String(status.id))
                )
              }
              selectedOptions={
                chainStatusOptions.filter((obj) =>
                  tmpFilter?.status?.includes(String(obj.id))
                ) || []
              }
              darkMode
              allowClear
            />
          </div>

          {user.temporaryStorageEnabled && (
            <div className={styles.unCollectFilterBox}>
              <SelectFieldMultipleChoise
                options={tempStorageCustomOptions}
                name="temporaryStorages"
                placeholder={t('un_collect.temporary_storage') ?? ''}
                onChange={(storages) =>
                  onChangeFilter(
                    'temporaryStorageIds',
                    storages.map((storage) => String(storage.id))
                  )
                }
                selectedOptions={
                  tempStorageCustomOptions.filter((obj) =>
                    tmpFilter?.temporaryStorageIds?.includes(String(obj.id))
                  ) || []
                }
                darkMode
                allowClear
                searchable
              />
            </div>
          )}

          <div className={styles.unCollectFilterBox}>
            <TableFilterCalendar
              containerClassName={styles.DateRangePicker}
              expanded={{
                placeholder: t('common.placeholder.select_date_range'),
                icon: <IconCalendarTablet />,
              }}
              allowClear={true}
              onChange={(values) => {
                setTmpFilter((prev) => ({
                  ...prev,
                  dateFrom: values?.[0]
                    ? getSlaFormatYearMonthDate(values[0])
                    : '',
                  dateTo: values?.[1]
                    ? getSlaFormatYearMonthDate(values[1])
                    : '',
                }));
              }}
              value={[tmpFilter?.dateFrom || '', tmpFilter?.dateTo || '']}
              endDateRequired={true}
              align="right"
              portalPositionPoint={(ref) => ref}
            />
          </div>

          <button
            className={styles.buttonApply}
            onClick={() => handleSubmitFilter()}
          >
            {t('dashboard.button_search')}
          </button>
        </div>
      </div>

      <table
        className={mergeClasses(styles.tableUnCollectList, 'primary-table')}
      >
        <thead>
          <tr>
            <th onClick={allChecked} className={styles.selection}>
              <div className={styles.checkAllContainer}>
                <span className={styles.checkAll}>
                  {t('common.collective_selection')}
                </span>
              </div>
            </th>
            <th className={styles.description}>{t('common.item')}</th>
            <th className={styles.weight}>{t('common.weight')}</th>
            <th className={styles.tenant}>{t('tenant.thead_tenant')}</th>
            {user.temporaryStorageEnabled && (
              <>
                <th className={styles.temporaryStorage}>
                  {t('emission_qr_scanning.temporary_storage')}
                </th>
                <th className={styles.datetime}>
                  {t('emission_qr_scanning.storage_updated_at')}
                </th>
              </>
            )}
            <th className={styles.datetime}>
              {t('history_emission_card.card_discharge_date_time')}
            </th>
          </tr>
        </thead>
        <tbody>
          {emissions && emissions.length > 0 ? (
            emissions.map((emission, i) => {
              const renderTemporaryStorage = () => {
                if (!emission.temporaryStorage) {
                  return null;
                }

                const color =
                  TEMPORARY_STORAGE_COLORS_PALETTE[
                    Number(emission.temporaryStorage.color) %
                      TEMPORARY_STORAGE_COLORS_PALETTE.length
                  ];

                return (
                  <span
                    className={styles.temporaryStorageTag}
                    style={{
                      color:
                        getBrightnessFromColor(color) <= 100
                          ? '#ffffff'
                          : '#1b1b1b',
                      backgroundColor: color,
                    }}
                  >
                    {emission.temporaryStorage.name}
                  </span>
                );
              };

              return (
                <tr key={i}>
                  <td className={styles.selection}>
                    {emission.nextStage && (
                      <CustomCheckBox
                        id={emission.id}
                        checked={checkedItems[emission.id] ?? false}
                        onChange={handleChange}
                        darkMode
                      />
                    )}
                  </td>
                  <td className={styles.description}>
                    <div className={styles.unCollectContent}>
                      <p>{emission.description}</p>
                      <UnCollectChainTag
                        collectionOperatorName={emission.collectionOperatorName}
                        emissionStatus={emission.emissionStatus}
                      />
                    </div>
                  </td>
                  <td className={styles.weight}>
                    {normalizeNumber({ value: emission.weight })}&nbsp;kg
                  </td>
                  <td className={styles.tenant}>{emission.tenant?.name}</td>
                  {user.temporaryStorageEnabled && (
                    <>
                      <td className={styles.temporaryStorage}>
                        {renderTemporaryStorage()}
                      </td>
                      <td className={styles.datetime}>
                        {emission.storageUpdatedAt
                          ? getUtcFormatDateTime(
                              emission.storageUpdatedAt,
                              DATE_FORMAT.slaYMDHm
                            )
                          : null}
                      </td>
                    </>
                  )}
                  <td className={styles.datetime}>
                    {/* BE return time with JP timezone but has Z in the end of string -> must show UTC to keep data correct */}
                    {getUtcFormatDateTime(
                      emission.datetime,
                      DATE_FORMAT.slaYMDHm
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={7} className={styles.msgError}>
                {t('messages.M_008')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
